import type { IDocumentAssemblerRenderedError } from '@commenda/api';
import { Alert, Badge } from '@commenda/kakashi';
import { pluralize } from '@commenda/utils';

import { DocumentAssemblerFindingCard } from './DocumentAssemblerFindingCard';

interface DocumentAssemblerFindingsListProps {
  findings: IDocumentAssemblerRenderedError[];
  hasPassedValidation: boolean;
  /** Rendered as the banner's action when the version's file can still be replaced. */
  onFileReplace?: () => void;
}

export const DocumentAssemblerFindingsList = ({
  findings,
  hasPassedValidation,
  onFileReplace,
}: DocumentAssemblerFindingsListProps) => {
  if (hasPassedValidation) return null;

  const replaceAction = onFileReplace
    ? {
        label: 'Replace the file',
        onClick: onFileReplace,
        variant: 'secondary' as const,
        size: 'sm' as const,
      }
    : undefined;

  return (
    <div className="flex flex-col gap-lg rounded-xl border border-border-primary p-xl">
      <div className="flex flex-wrap items-center gap-sm">
        <h3 className="txt-medium-medium text-text-primary">Problems</h3>
        {findings.length > 0 ? (
          <Badge variant="error" size="sm">
            {findings.length}
          </Badge>
        ) : null}
      </div>

      {findings.length === 0 ? (
        // A version can be unusable with nothing to show: the server returns errors: [] when a
        // stored check result cannot be parsed. Without this branch the heading would read
        // "0 problems" above an empty list, next to a version that cannot be published.
        <Alert
          variant="destructive"
          title="This file could not be checked"
          description="The check result stored for this version cannot be read, so there is nothing to list. Replace the file to have it read again."
          action={replaceAction}
        />
      ) : (
        <>
          <Alert
            variant="destructive"
            title={`This file cannot be used — ${pluralize(findings.length, 'problem')} to fix`}
            description="Everything found in the file is listed below. There is nothing else waiting to be revealed once you fix these."
            action={replaceAction}
          />

          <div className="flex flex-col gap-md">
            {findings.map((finding, index) => (
              <DocumentAssemblerFindingCard
                // Findings carry no id and the same code legitimately repeats, so the identity is
                // the code plus its position in the server-ordered array.
                key={`${finding.code}-${finding.documentPart}-${index}`}
                finding={finding}
                position={index + 1}
                total={findings.length}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};
